import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo, type ReactNode } from 'react';
import { usePrefetchRoles } from '#api/roles.queries.ts';
import { InfoCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { HostsCountLink } from '#components/patterns/entity-links/hosts-count-link/HostsCountLink.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import { RoleName } from '#components/patterns/role-name/RoleName.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { allowedHostsFilter, hasAllowedRole } from '#screens/roles/detail/allowed-hosts-filter/allowedHostsFilter.ts';
import { stringifyPortRange } from '../stringify-port-range/stringifyPortRange.ts';
import type { FirewallRuleFieldValue, FirewallRuleSource } from '../types.ts';
import { hasAnyAnyAllRule } from './hasAnyAnyAllRule.ts';
import styles from './FirewallRuleList.module.css';

/** Rows may optionally carry a source (host detail view), which adds a Source column. */
type Data = FirewallRuleFieldValue & { source?: FirewallRuleSource };

const columnHelper = createColumnHelper<Data>();

type Props = {
  firewallRules: Data[];
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  className?: string;
  hideOverflowX?: boolean;
  /**
   * Shrink the wide columns so every column stays on screen in tight contexts. The caller owns the
   * media query, since the threshold depends on the chrome around the table, not on the table.
   */
  narrow?: boolean;
  /**
   * Message shown in the info banner when there are no firewall rules. Defaults to role-oriented
   * copy; pass a nullish value to suppress the empty-state banner entirely.
   */
  emptyStateMessage?: ReactNode;
  /**
   * Counts and links the hosts each rule allows, within this network. Only meaningful where the
   * rules apply to one host: a host only accepts peers from its own network, while a role or tag
   * spans every network, so there is no single count to show.
   */
  networkID?: string;
};

export function FirewallRuleList({
  firewallRules,
  hideOverflowX,
  narrow,
  onRemove,
  onEdit,
  onDuplicate,
  className,
  networkID,
  emptyStateMessage = 'Hosts with this role will not be reachable by any other hosts. Add firewall rules to allow traffic if desired.',
}: Props) {
  usePrefetchRoles();
  const location = useAppLocation();

  // Rules carry a source only in the host detail view, where rules from the role and tags are merged.
  const hasSources = firewallRules.some((rule) => rule.source);

  const columns = useMemo(() => {
    const cols = [
      columnHelper.display({
        header: 'Allowed hosts',
        // The count link never truncates, so it gets its own room rather than squeezing the role name.
        // FirewallCard's narrow breakpoint is derived from these widths; update it when they change.
        size: (narrow ? 240 : 300) + (networkID ? 60 : 0),
        cell(info) {
          const { allowedRoleID, tags } = info.row.original;

          const hasRole = hasAllowedRole(allowedRoleID);

          return (
            <div className="flex align-baseline nowrap gap-8">
              {hasRole ?
                <span className="flex gap-4 mw-0">
                  <RoleName className="inline truncate padr-0" roleID={allowedRoleID} />
                  {`hosts ${tags.length ? 'tagged ' : ''}`}
                </span>
              : `All hosts ${tags.length ? 'tagged ' : ''}`}
              {tags.length ?
                <InlineTagsList tagNames={tags} truncateAfter={1} />
              : null}
              {networkID ?
                <HostsCountLink filter={allowedHostsFilter(info.row.original, networkID)} />
              : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('protocol', {
        header: 'Protocol',
        size: 80,
        cell(info) {
          const proto = info.getValue();
          return proto === 'ANY' ? 'Any' : proto;
        },
      }),
      columnHelper.accessor('portRange', {
        header: 'Port range',
        size: 100,
        cell(info) {
          const value = info.getValue();
          return stringifyPortRange(value);
        },
      }),

      columnHelper.accessor('description', {
        header: 'Description',
        // The fill column: free text soaks up whatever width the sized columns leave over, and
        // gives it back first when space runs out; the size is only its floor.
        size: 150,
        meta: {
          truncate: true,
          fill: true,
        },
      }),
    ];

    if (hasSources) {
      cols.push(
        columnHelper.display({
          id: 'source',
          header: 'Source',
          size: narrow ? 130 : 160,
          meta: { truncate: true },
          cell(info) {
            const { source } = info.row.original;
            if (!source) return null;
            // Both roles and tags link to their detail page; role sources are labelled "Role:".
            const nameLink =
              source.to ?
                <Link className="a inline truncate" to={source.to} state={{ from: location }}>
                  {source.label}
                </Link>
              : source.label;
            return source.kind === 'role' ? <span className="flex gap-4 nowrap">Role: {nameLink}</span> : nameLink;
          },
        })
      );
    }

    if (onEdit && onRemove) {
      cols.push(
        columnHelper.display({
          id: 'action-menu',
          header: '',
          size: 34,
          meta: {
            className: 'meatball-menu',
          },
          cell(info) {
            const { id } = info.row.original;
            return (
              <div className="flex justify-end">
                <MeatballMenu>
                  <MenuItem onClick={() => onEdit(id)}>Edit</MenuItem>
                  {onDuplicate ?
                    <MenuItem onClick={() => onDuplicate(id)}>Duplicate</MenuItem>
                  : null}
                  <MenuItem onClick={() => onRemove(id)}>Delete</MenuItem>
                </MeatballMenu>
              </div>
            );
          },
        })
      );
    }

    return cols;
  }, [onEdit, onRemove, onDuplicate, hasSources, narrow, networkID, location]);

  if (!firewallRules.length) {
    // A nullish emptyStateMessage suppresses the empty-state banner entirely.
    if (!emptyStateMessage) {
      return null;
    }
    return (
      <Banner variant="info" Icon={InfoCircle} className={cn('mb-16', className)}>
        {emptyStateMessage}
      </Banner>
    );
  }

  const hasOverlyBroadRule = hasAnyAnyAllRule(firewallRules);

  return (
    <>
      {hasOverlyBroadRule && firewallRules.length >= 2 ?
        <Banner variant="info" Icon={InfoCircle} className="mb-24">
          You have a rule allowing all hosts on any protocol and port. Because it permits all traffic, it makes more
          specific rules ineffective.
        </Banner>
      : null}
      <div className={className}>
        <Table
          className={cn(styles.FirewallRuleList, { [styles.FirewallRuleList___hasAnyAny]: hasOverlyBroadRule })}
          data={firewallRules}
          columns={columns}
          hideOverflowX={hideOverflowX}
          fixedLayout={true}
        />
      </div>
    </>
  );
}
